import { buildPoints, mapFittings, validSegs } from "@/lib/pipe3dPoints";

// EN 10220 outside diameters (mm) by DN.
const OD = { 15: 21.3, 20: 26.9, 25: 33.7, 32: 42.4, 40: 48.3, 50: 60.3, 65: 76.1, 80: 88.9, 100: 114.3, 125: 139.7, 150: 168.3, 200: 219.1, 250: 273, 300: 323.9, 350: 355.6, 400: 406.4 };

export function outsideDiameter(diameter) {
  const s = String(diameter || "").replace(",", ".");
  const n = parseFloat(s.replace(/[^\d.]/g, ""));
  if (!(n > 0)) return null;
  if (/dn/i.test(s) || OD[n]) return OD[n] || null;
  return n;
}

const deflections = (pts) => {
  const out = [];
  for (let i = 1; i < pts.length - 1; i++) {
    const a = pts[i].map((v, k) => v - pts[i - 1][k]);
    const b = pts[i + 1].map((v, k) => pts[i + 1][k] - pts[i][k]);
    const cos = a.reduce((s, v, k) => s + v * b[k], 0) / (Math.hypot(...a) * Math.hypot(...b));
    out.push((Math.acos(Math.max(-1, Math.min(1, cos))) * 180) / Math.PI);
  }
  return out;
};

// Elbow take-off (centre to weld end) for a deflection angle.
export function takeOff(deg, cfg, od) {
  if (deg < 5) return 0;
  if (cfg.mode === "custom") {
    if (Math.abs(deg - 90) <= 10 && cfg.a90 > 0) return cfg.a90;
    if (Math.abs(deg - 45) <= 10 && cfg.a45 > 0) return cfg.a45;
    const r = cfg.a90 > 0 ? cfg.a90 : 0;
    return r * Math.tan((deg * Math.PI) / 360);
  }
  if (!od) return 0;
  return (cfg.mode === "2d" ? 1 : 1.5) * od * Math.tan((deg * Math.PI) / 360);
}

/** Returns per-segment cut pieces (mm) after elbows, fittings and weld gaps. */
export function cutPlan(segments, fittings, diameter, cfg, fitLens) {
  const segs = validSegs(segments);
  const pts = buildPoints(segments) || [];
  const od = outsideDiameter(diameter);
  const angles = deflections(pts);
  const fits = mapFittings(segments, fittings).map((f, i) => ({ ...f, idx: i, len: Number(fitLens[i]) > 0 ? Number(fitLens[i]) : 0 }));
  const gap = Number(cfg.gap) > 0 ? Number(cfg.gap) : 0;
  return segs.map((s, i) => {
    const L = Number(s.length_mm) > 0 ? Number(s.length_mm) : null;
    if (L == null) return { n: i + 1, L: null, pieces: [] };
    const startBend = i > 0 && angles[i - 1] >= 5;
    const endBend = i < segs.length - 1 && angles[i] >= 5;
    const start = startBend ? takeOff(angles[i - 1], cfg, od) : 0;
    const end = L - (endBend ? takeOff(angles[i], cfg, od) : 0);
    const own = fits.filter((f) => f.seg === i);
    const unknown = own.filter((f) => f.pos == null);
    const placed = own.map((f) => ({ ...f, at: f.pos != null ? f.pos * 1000 : (L * (unknown.indexOf(f) + 1)) / (unknown.length + 1) }))
      .sort((a, b) => a.at - b.at);
    const bounds = [{ x: start, weld: startBend }];
    placed.forEach((f) => { bounds.push({ x: f.at - f.len / 2, weld: true }, { x: f.at + f.len / 2, weld: true }); });
    bounds.push({ x: end, weld: endBend });
    const pieces = [];
    for (let k = 0; k < bounds.length; k += 2) {
      const a = bounds[k]; const b = bounds[k + 1];
      const v = Math.round(b.x - a.x - (gap / 2) * ((a.weld ? 1 : 0) + (b.weld ? 1 : 0)));
      // A fitting sitting right at a free route end leaves no pipe there — skip, don't flag.
      const freeEnd = (k === 0 && !a.weld) || (k + 1 === bounds.length - 1 && !b.weld);
      if (freeEnd && v <= 0 && placed.length) continue;
      pieces.push(v);
    }
    return { n: i + 1, L, pieces };
  });
}
